import { type LDContextCommon, LDProvider, type ProviderConfig, useLDClient } from 'launchdarkly-react-client-sdk';
import React, { type PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { useAuth } from '@sine/auth-client';
import { useTeamSwitcher } from '@sine/sine-team-switcher';

import pendoService from '../../authentication/pendo.service';
import type { UserData } from '../../authentication/pendo.types';
import rxEventHub, { RxEvent } from '../../services/events/rx';
import teamSwitcherService from '../../team/teamSwitcher.service';

export interface LaunchDarklyContextType {
  updateContext: (newContext?: LDContextCommon) => void;
}

const defaultLaunchDarklyContext: LaunchDarklyContextType = {
  updateContext: () => undefined,
};

const LaunchDarklyContext = createContext(defaultLaunchDarklyContext);

function LDTeamSwitcher({ children }: PropsWithChildren) {
  const ldClient = useLDClient();
  const { userDetails: user } = useAuth();
  const { currentTeam } = useTeamSwitcher();

  const updatePendoMetadata = useCallback(async () => {
    if (ldClient && currentTeam) {
      const flags = await ldClient.allFlags();

      // strip out the hyphen from the flag names
      Object.keys(flags).forEach((key) => {
        const newKey = 'ldFlag_' + key.replace(/-/g, '');
        flags[newKey] = flags[key];
        delete flags[key];
      });

      await pendoService.identifyPendoMetadata(user as UserData, {
        ...currentTeam,
        ...flags,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeam, ldClient?.allFlags()]);

  useEffect(() => {
    // TODO: use react-query
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    updatePendoMetadata();
  }, [updatePendoMetadata]);

  useEffect(() => {
    const teamChangeSubscription = rxEventHub.on(RxEvent.TEAM_CHANGED, updatePendoMetadata);

    return () => {
      teamChangeSubscription.unsubscribe();
    };
  }, []);

  const defaultContextValue = useMemo(
    () => ({
      user: currentTeam
        ? {
            key: currentTeam.id,
            name: currentTeam.name,
          }
        : undefined,
      team: currentTeam ? { key: currentTeam.id, name: currentTeam.name } : undefined,
      environment: { key: window.env.ENV_NAME },
    }),
    [currentTeam],
  );

  const updateContext = useCallback(
    (newContext?: LDContextCommon) => {
      if (ldClient) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        ldClient.identify({
          kind: 'multi',
          ...defaultContextValue,
          ...newContext,
        });
      }
    },
    [defaultContextValue, ldClient],
  );

  useEffect(() => {
    // re-initialise Launch Darkly when users switch teams
    updateContext();
  }, [updateContext]);

  const value = useMemo<LaunchDarklyContextType>(
    () => ({
      updateContext,
    }),
    [updateContext],
  );

  return <LaunchDarklyContext.Provider value={value}>{children}</LaunchDarklyContext.Provider>;
}

export default function LaunchDarklyProvider({ children }: PropsWithChildren<unknown>) {
  const currentTeam = teamSwitcherService.getCurrentTeam();

  const props = useMemo<ProviderConfig>(
    () => ({
      clientSideID: window.env.REACT_APP_LAUNCH_DARKLY_CLIENT_SIDE_ID,
      context: {
        kind: 'user',
        key: currentTeam?.id,
        name: currentTeam?.name,
      },
    }),
    [currentTeam],
  );

  return (
    <LDProvider {...props}>
      <LDTeamSwitcher>{children}</LDTeamSwitcher>
    </LDProvider>
  );
}

export function useLaunchDarklyContext() {
  return useContext(LaunchDarklyContext);
}
